import { forwardRef, useEffect, useState } from 'react';
import { cn, cx } from '@/utils';
import { Spinner } from '@material-tailwind/react';
import { cva, type VariantProps } from 'class-variance-authority';
import _debounce from 'lodash/debounce';

import SvgClose from '../common/Icons/Close';
import SvgSearch from '../common/Icons/Search';

const SearchInputVariants = cva('', {
  variants: {
    color: {
      default: 'border-tundora text-dimGray',
      primary: 'border-primary text-primary',
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

export type InputProps = {
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement | null;
  isTyping?: boolean;
  resultsInfo?: any;
  onClear?: () => void;
  onSearch?: () => void;
  inputClassname?: string;
  iconRightClassName?: string;
  iconCloseClassName?: string;
  dividerClassName?: string;
} & React.InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof SearchInputVariants>;

/**
 * SearchInput
 * @param {React.ReactElement} iconLeft - Icon to be displayed on the left side of the input
 * @param {React.ReactElement | null} iconRight - Icon to be displayed on the right side of the input
 * @param {() => void} onClear
 * @param {() => void} onSearch
 * @param {React.InputHTMLAttributes<HTMLInputElement>} props - Props to be passed to the input
 * @returns {React.ReactElement} - Returns the SearchInput component
 */
const SearchInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      iconLeft,
      iconRight,
      resultsInfo,
      onClear,
      onSearch,
      color,
      isTyping,
      inputClassname,
      iconRightClassName,
      iconCloseClassName,
      dividerClassName,
      ...props
    },
    ref
  ) => {
    const [isTypingg, setIsTypingg] = useState(false);

    const debouncedHandleChange = _debounce((value) => {
      setIsTypingg(false);
    }, 700);

    useEffect(() => {
      setIsTypingg(true);
      debouncedHandleChange(props.value);
      return () => {
        debouncedHandleChange.cancel();
      };
    }, [props.value]);

    return (
      <>
        <div
          className={cx(
            'relative flex h-[42px] w-full items-center gap-5 rounded-full border px-5 py-[3px] shadow-md shadow-primary-dark',
            SearchInputVariants({ color }),
            !iconRight && 'pr-[3px]',
            iconLeft && 'pl-[3px]',
            className
          )}
        >
          {iconLeft && (
            <div className="flex h-[36px] w-[40px]  items-center justify-center px-[10px]">
              {iconLeft}
            </div>
          )}
          <input
            className={cn('body-text1 h-[36px] w-full text-black outline-none', inputClassname)}
            ref={ref}
            {...props}
          />
          {props.value && (
            <button onClick={onClear} className={cx('relative', iconRight === null && 'pr-[10px]')}>
              <SvgClose className={cn('cursor-pointer text-dimGray', iconCloseClassName)} />
              {iconRight !== null && (
                <span
                  className={cn(
                    'absolute right-[-11px] top-[-9px] h-[34px] w-[2px] bg-dimGray',
                    dividerClassName
                  )}
                ></span>
              )}
            </button>
          )}

          {isTypingg ? (
            <Spinner className="mr-2" width={30} height={30} />
          ) : (
            iconRight !== null && (
              <div
                className={cn(
                  'h-[36px] items-center justify-center',
                  SearchInputVariants({ color }),
                  iconRightClassName
                )}
              >
                <SvgSearch className="cursor-pointer" />
              </div>
            )
          )}
        </div>

        {resultsInfo}
      </>
    );
  }
);
SearchInput.displayName = 'SearchInput';

export { SearchInput };
